import { useEffect, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import type { WhitelabelPaymentHelpMethod } from "../../guides/whitelabelPaymentHelp/paymentHelpSteps";

export type WhitelabelHelpModalProps = {
  open: boolean;
  onClose: () => void;
  onSelectCreateWhitelabelTour: () => void;
  onSelectPaymentHelpTour: (method: WhitelabelPaymentHelpMethod) => void;
  onSelectAddFundsTour: () => void;
};

type HelpView = "root" | "payments";

export function WhitelabelHelpModal({
  open,
  onClose,
  onSelectCreateWhitelabelTour,
  onSelectPaymentHelpTour,
  onSelectAddFundsTour,
}: WhitelabelHelpModalProps) {
  const [view, setView] = useState<HelpView>("root");

  useEffect(() => {
    if (!open) return;
    setView("root");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = view === "payments" ? "Payment help" : "Help";
  const subtitle =
    view === "payments"
      ? "Pick how you plan to pay after creating a whitelabel."
      : "Choose an option";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4 py-8"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--border-color)_35%,#3f4f52)] bg-zinc-950 shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wl-help-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800/90 bg-zinc-900 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            {view === "payments" ? (
              <button
                type="button"
                aria-label="Back"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
                onClick={() => setView("root")}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: 22 }} />
              </button>
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-600/60 bg-zinc-800/80 text-zinc-200">
                <HelpOutlineRoundedIcon sx={{ fontSize: 22 }} />
              </span>
            )}
            <h2
              id="wl-help-modal-title"
              className="m-0 truncate text-lg font-semibold tracking-tight text-zinc-100"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
            onClick={onClose}
          >
            <CloseRoundedIcon sx={{ fontSize: 22 }} />
          </button>
        </div>

        <div className="px-4 pb-5 pt-4 sm:px-5 sm:pb-6 sm:pt-5">
          <p className="m-0 text-base font-semibold text-zinc-100">{subtitle}</p>
          {view === "root" ? (
            <>
              <p className="mt-1 text-sm leading-snug text-zinc-500">
                Open a guided walkthrough. You can skip anytime.
              </p>

              <button
                type="button"
                className="mt-5 flex w-full items-stretch gap-3 rounded-xl border border-[color-mix(in_srgb,var(--border-color)_40%,#334155)] bg-[color-mix(in_srgb,var(--border-color)_10%,#0c1220)] p-3.5 text-left transition hover:border-[color-mix(in_srgb,var(--border-color)_55%,#475569)] hover:bg-[color-mix(in_srgb,var(--border-color)_14%,#0f172a)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={onSelectCreateWhitelabelTour}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-snug text-zinc-100">
                    Create Branding tour
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                   A guild to walkthrough how you can create your brand
                  </p>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full border border-zinc-600/70 bg-zinc-900/90 text-zinc-200">
                  <ArrowForwardRoundedIcon sx={{ fontSize: 22 }} />
                </span>
              </button>

              <button
                type="button"
                className="mt-3 flex w-full items-stretch gap-3 rounded-xl border border-[color-mix(in_srgb,var(--border-color)_40%,#334155)] bg-[color-mix(in_srgb,var(--border-color)_10%,#0c1220)] p-3.5 text-left transition hover:border-[color-mix(in_srgb,var(--border-color)_55%,#475569)] hover:bg-[color-mix(in_srgb,var(--border-color)_14%,#0f172a)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={() => setView("payments")}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-snug text-zinc-100">
                    Payments
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                    How to complete plan payment after creating a brand or brand
                    top-up.
                  </p>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full border border-zinc-600/70 bg-zinc-900/90 text-zinc-200">
                  <ArrowForwardRoundedIcon sx={{ fontSize: 22 }} />
                </span>
              </button>

              <button
                type="button"
                className="mt-3 flex w-full items-stretch gap-3 rounded-xl border border-[color-mix(in_srgb,var(--border-color)_40%,#334155)] bg-[color-mix(in_srgb,var(--border-color)_10%,#0c1220)] p-3.5 text-left transition hover:border-[color-mix(in_srgb,var(--border-color)_55%,#475569)] hover:bg-[color-mix(in_srgb,var(--border-color)_14%,#0f172a)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={onSelectAddFundsTour}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-snug text-zinc-100">Add Funds</p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                    How to add funds to your brand
                  </p>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full border border-zinc-600/70 bg-zinc-900/90 text-zinc-200">
                  <ArrowForwardRoundedIcon sx={{ fontSize: 22 }} />
                </span>
              </button>
            </>
          ) : (
            <div className="mt-4 space-y-2.5">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-700/80 bg-zinc-900/60 px-4 py-3 text-left text-sm font-semibold text-zinc-100 transition hover:bg-zinc-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={() => onSelectPaymentHelpTour("crypto")}
              >
                Crypto
                <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              </button>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-700/80 bg-zinc-900/60 px-4 py-3 text-left text-sm font-semibold text-zinc-100 transition hover:bg-zinc-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={() => onSelectPaymentHelpTour("fiat")}
              >
                UPI / Bank (Fiat)
                <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              </button>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-700/80 bg-zinc-900/60 px-4 py-3 text-left text-sm font-semibold text-zinc-100 transition hover:bg-zinc-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                onClick={() => onSelectPaymentHelpTour("offline")}
              >
                Offline
                <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
